import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, finalize, map, share, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../destroyable.component';
import {FormGeneratorService} from '../form-generator/form-generator.service';
import {LocalizationService} from '../localization/localization.service';
import {GPTranslateService} from '../localization/translate.service';

import {ContractAlert, NO_ZONE_ACCESS, Zone} from './contract-alerts.model';
import {ContractAlertsService} from './contract-alerts.service';

@Component({
  selector: 'gp-contract-alerts',
  templateUrl: './contract-alerts.component.html',
  styleUrls: ['./contract-alerts.component.scss']
})
export class ContractAlertsComponent extends Destroyable implements OnInit {
  public readonly NO_ZONE_ACCESS = NO_ZONE_ACCESS;

  @Input() public tableOnly = false;

  public loading: boolean = true;
  public alerts$: Observable<ContractAlert[]>;

  private fetchAlertsSubject = new BehaviorSubject<boolean>(null);


  public filtersVisible: boolean = false;
  public category: string = '';
  public triggerLanguageChange$: Observable<number>;
  public triggerDateChange$: Observable<number>;

  private contractId: number;

  constructor(
    private translate: GPTranslateService,
    private localizationService: LocalizationService,
    private alertsService: ContractAlertsService,
    private formGeneratorService: FormGeneratorService,
    private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.fetchAlertsSubject.asObservable().pipe(
      filter(value => !!value),
      tap(() => {
        this.alerts$ = this.fetchContractAlerts();
        this.cd.detectChanges();
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.triggerLanguageChange$ = this.translate.onLangChange.pipe(
      map(() => Math.random()), share());
    this.triggerDateChange$ = this.localizationService.changeDateFormatEvent.pipe(
      map(() => Math.random()), share());
  }

  public load(contractId: number) {
    if (!this.contractId || this.contractId !== contractId) {
      this.contractId = contractId;
      this.fetchAlertsSubject.next(true);
    }
  }

  public updateZoneName(zoneName?: string) {
    if (zoneName) {
      this.formGeneratorService.updateZoneName(zoneName);
    }
  }

  private fetchContractAlerts(): Observable<ContractAlert[]> {
    if (!this.contractId) {
      return;
    }
    this.loading = true;
    return this.alertsService.getAlerts(this.contractId)
      .pipe(
        map((response: ContractAlert[]) => {
          return this.groupNonAccessAlerts(response).map(alert => {
            // Sort the zones array to display zones in this order first emp zones and then rep zones
            alert.zones = this.sortZones(alert.zones);
            return alert;
          });
        }),
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        })
      );
  }

  /**
   * Sorts an array of zones based on their metadata's structureType property.
   * The sorting is performed in ascending order, with zones having an 'EMP'
   * structureType coming before those with a 'REP' structureType. If a zone's
   * metadata is undefined, it is treated as having an empty string for the
   * structureType, ensuring that such zones are sorted accordingly.
   *
   * @param zones - An array of Zone objects to be sorted.
   * @returns The sorted array of Zone objects.
   */
  private sortZones(zones: Zone[] | null | undefined): Zone[] {
    if (!zones) {
      return [];
    }

    return zones.sort((a, b) => {
      const structureTypeA = a.metadata?.structureType ?? '';
      const structureTypeB = b.metadata?.structureType ?? '';
      return structureTypeA.localeCompare(structureTypeB);
    });
  }


  private groupNonAccessAlerts(alerts: ContractAlert[]): ContractAlert[] {
    let noAccessZoneCount = 0;
    let noAccessAlertCreationDate = '';

    const newAlerts: ContractAlert[] = alerts.filter(alert => {
      if (!alert.zones && !alert.transId) {
        noAccessZoneCount++;
        noAccessAlertCreationDate = alert.creationDate;
        return false;
      }
      return true;
    });

    if (noAccessZoneCount > 0) {
      newAlerts.push(this.createNoAccessAlert(noAccessAlertCreationDate));
    }
    return newAlerts;

  }

  private createNoAccessAlert(creationDate: string): ContractAlert {
    return {
      beginDate: null,
      creationDate: creationDate,
      endDate: null,
      name: NO_ZONE_ACCESS.ALERT_NAME,
      transId: NO_ZONE_ACCESS.MESSAGE,
      zones: [
        {name: NO_ZONE_ACCESS.ZONE_NAME, transIds: [NO_ZONE_ACCESS.CATEGORY], metadata: null}
      ],
      blocking: null
    };
  }

}
